import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { BaseFormControl } from '../base-control';
import { FormImageField } from '../form-field';
import { ImageService } from '../../../global/images/image.service';
import { HttpClientWithInFlightCache } from '../../../global/httpClient';
import { FileBrowserModalComponent } from '../../file-browser/file-browser-modal.component';
import { ImageCropperModal } from '../../../global/images/image-cropper.modal';
import { UploadStateService } from './upload-state.service';

@Component({
    standalone: false,
    selector: 'abs-file-upload',
    template: `
        <div class="form-group file-upload" *ngIf="parentFormGroup" [formGroup]="parentFormGroup">
            <label *ngIf="!field.hideLabel">
                {{ field.label || ($any(field.name) | formLabel) }}
                <ion-icon class="info" *ngIf="field.description" name="information-circle" (click)="showDescription($event)"></ion-icon>
            </label>

            <div class="image-preview" *ngIf="previewUrl">
                <img [src]="previewUrl" (load)="onImageLoad()" (error)="onImageError()" [style.display]="imageLoadError ? 'none' : ''" />
                <div class="image-error-placeholder" *ngIf="imageLoadError">
                    <ion-icon name="image-outline"></ion-icon>
                    <span>Preview unavailable</span>
                </div>
            </div>

            <div class="image-controls">
                <input
                    type="file"
                    #fileInput
                    [accept]="field.accept || 'image/*'"
                    (change)="onFileSelected($event)"
                    style="display: none"
                />
                <ion-button class="btn-sm" (click)="fileInput.click()" [disabled]="uploading">
                    {{ previewUrl ? 'Replace' : field.crop ? 'Upload & Crop' : 'Upload' }}
                </ion-button>
                <ion-button *ngIf="hasSourceImage" class="btn-sm" color="tertiary" (click)="reCrop()" [disabled]="uploading">
                    Re-crop
                </ion-button>
                <ion-button class="btn-sm" color="secondary" (click)="browseExisting()" [disabled]="uploading">
                    Browse Existing
                </ion-button>
                <ion-button class="btn-sm" color="danger" *ngIf="previewUrl" (click)="removeImage()" [disabled]="uploading">
                    Remove
                </ion-button>
                <ion-spinner *ngIf="uploading" name="crescent"></ion-spinner>
                <span class="upload-error" *ngIf="uploadError">{{ uploadError }}</span>
            </div>

            <div class="crop-help" *ngIf="field.crop">Uploaded images are cropped to {{ field.crop.width }}×{{ field.crop.height }}.</div>
        </div>
    `,
    styles: [
        `
            .image-preview {
                margin: 8px 0;
            }
            .image-preview img {
                max-width: 200px;
                max-height: 200px;
                border-radius: 4px;
                border: 1px solid #ccc;
                object-fit: contain;
            }
            .image-error-placeholder {
                display: flex;
                align-items: center;
                gap: 6px;
                width: 200px;
                height: 60px;
                border-radius: 4px;
                border: 1px dashed #ccc;
                justify-content: center;
                color: #999;
                font-size: 0.82em;
            }
            .image-controls {
                display: flex;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
            }
            .upload-error {
                color: #dc3545;
                font-size: 0.85em;
            }

            .crop-help {
                margin-top: 8px;
                font-size: 0.85em;
                color: var(--ion-color-medium-shade, #666);
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadComponent extends BaseFormControl implements OnInit {
    private imageService = inject(ImageService);
    private http = inject(HttpClientWithInFlightCache);
    private cdr = inject(ChangeDetectorRef);
    private fileModalController = inject(ModalController);
    private uploadState = inject(UploadStateService);

    @Input()
    field: FormImageField;

    previewUrl: string | null = null;
    sourcePreviewUrl: string | null = null;
    imageLoadError = false;
    uploading = false;
    uploadError: string | null = null;

    /** Set the local uploading flag and keep the shared UploadStateService in sync. */
    private setUploading(value: boolean) {
        if (value === this.uploading) return;
        this.uploading = value;
        value ? this.uploadState.begin() : this.uploadState.end();
    }

    /** True when the source field has a saved image ID — used to show the Re-crop button
     *  regardless of whether the image URL successfully loaded in the browser. */
    get hasSourceImage(): boolean {
        return !!(this.field?.sourceField && this.parentFormGroup?.get(this.field.sourceField)?.value);
    }

    ngOnInit() {
        super.ngOnInit();
        this.updatePreview();

        // Keep preview in sync if the control value changes externally
        this.parentFormGroup.get(this.field.name)?.valueChanges.subscribe(() => {
            this.updatePreview();
            this.cdr.markForCheck();
        });

        if (this.field.sourceField) {
            this.updateSourcePreview();
            this.parentFormGroup.get(this.field.sourceField)?.valueChanges.subscribe(() => {
                this.updateSourcePreview();
                this.cdr.markForCheck();
            });
        }
    }

    async browseExisting() {
        const modal = await this.fileModalController.create({
            component: FileBrowserModalComponent
        });
        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data?.imageId) {
            this.parentFormGroup.get(this.field.name)?.setValue(data.imageId);
            this.parentFormGroup.get(this.field.name)?.markAsDirty();
            this.previewUrl = this.imageService.url(data.imageId);
            this.cdr.markForCheck();
        }
    }

    onFileSelected(event: Event) {
        void this.handleFileSelected(event);
    }

    private async handleFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        // Reset so selecting the same file again still triggers change
        input.value = '';

        if (this.field.crop) {
            const imageBase64 = await this.fileToBase64(file);
            const croppedBlob = await this.openCropper({ imageBase64 });
            if (!croppedBlob) return;

            const croppedFile = new File([croppedBlob], this.getCroppedFilename(file.name), {
                type: croppedBlob.type
            });

            if (this.field.sourceField) {
                // Upload original and cropped in parallel
                this.setUploading(true);
                this.uploadError = null;
                this.cdr.markForCheck();

                try {
                    const [originalRes, croppedRes] = await Promise.all([
                        this.uploadFileToApi(file, file.name),
                        this.uploadFileToApi(croppedFile, croppedFile.name)
                    ]);

                    this.parentFormGroup.get(this.field.sourceField)?.setValue(originalRes.id);
                    this.parentFormGroup.get(this.field.sourceField)?.markAsDirty();
                    if (this.field.sourceFileNameField) {
                        this.parentFormGroup.get(this.field.sourceFileNameField)?.setValue(file.name);
                        this.parentFormGroup.get(this.field.sourceFileNameField)?.markAsDirty();
                    }
                    this.parentFormGroup.get(this.field.name)?.setValue(croppedRes.id);
                    this.parentFormGroup.get(this.field.name)?.markAsDirty();
                    this.previewUrl = URL.createObjectURL(croppedBlob);
                    this.sourcePreviewUrl = this.imageService.url(originalRes.id);
                    this.imageLoadError = false;
                } catch (err) {
                    this.uploadError = 'Upload failed. Please try again.';
                    console.error('File upload error:', err);
                } finally {
                    this.setUploading(false);
                    this.cdr.markForCheck();
                }
            } else {
                this.uploadFile(croppedFile);
            }
        } else {
            this.uploadFile(file);
        }
    }

    async reCrop() {
        const sourceId = this.parentFormGroup.get(this.field.sourceField)?.value;
        if (!sourceId) return;

        const sourceFileName = this.field.sourceFileNameField ? this.parentFormGroup.get(this.field.sourceFileNameField)?.value : null;
        if (!sourceFileName) throw new Error('Source filename not found — re-crop requires a stored filename');

        const imageURL = this.imageService.url(sourceId);
        const croppedBlob = await this.openCropper({ imageURL });
        if (!croppedBlob) return;

        const croppedFileName = this.getCroppedFilename(sourceFileName);

        this.setUploading(true);
        this.uploadError = null;
        this.cdr.markForCheck();

        try {
            const res = await this.uploadFileToApi(croppedBlob, croppedFileName);
            this.parentFormGroup.get(this.field.name)?.setValue(res.id);
            this.parentFormGroup.get(this.field.name)?.markAsDirty();
            this.previewUrl = URL.createObjectURL(croppedBlob);
            this.imageLoadError = false;
        } catch (err) {
            this.uploadError = 'Upload failed. Please try again.';
            console.error('Re-crop upload error:', err);
        } finally {
            this.setUploading(false);
            this.cdr.markForCheck();
        }
    }

    private uploadFile(file: File) {
        const fileName = file.name;
        if (!fileName) throw new Error('Cannot upload file without a name');

        this.setUploading(true);
        this.uploadError = null;
        this.cdr.markForCheck();

        this.uploadFileToApi(file, fileName).then(
            (res) => {
                this.parentFormGroup.get(this.field.name)?.setValue(res.id);
                this.parentFormGroup.get(this.field.name)?.markAsDirty();
                this.previewUrl = URL.createObjectURL(file);
                this.imageLoadError = false;
                this.setUploading(false);
                this.cdr.markForCheck();
            },
            (err) => {
                this.uploadError = 'Upload failed. Please try again.';
                this.setUploading(false);
                this.cdr.markForCheck();
                console.error('File upload error:', err);
            }
        );
    }

    private uploadFileToApi(file: File | Blob, fileName: string): Promise<{ id: string }> {
        const formData = new FormData();
        formData.append('file', file, fileName);
        formData.append('gameId', this.formValue?.gameId || '');
        formData.append('category', this.field.category || 'unit-template');

        const url = `${this.config.apiBaseUrl}/images/upload`;
        return this.http.post(url, formData).toPromise() as Promise<{ id: string }>;
    }

    removeImage() {
        const control = this.parentFormGroup.get(this.field.name);
        control?.setValue(null);
        control?.markAsDirty();

        if (this.field.sourceField) {
            const sourceControl = this.parentFormGroup.get(this.field.sourceField);
            sourceControl?.setValue(null);
            sourceControl?.markAsDirty();
        }

        if (this.field.sourceFileNameField) {
            const sourceFileNameControl = this.parentFormGroup.get(this.field.sourceFileNameField);
            sourceFileNameControl?.setValue(null);
            sourceFileNameControl?.markAsDirty();
        }

        this.revokeBlobUrl(this.previewUrl);
        this.previewUrl = null;
        this.sourcePreviewUrl = null;
        this.imageLoadError = false;
        this.cdr.markForCheck();
    }

    onImageLoad() {
        if (this.imageLoadError) {
            this.imageLoadError = false;
            this.cdr.markForCheck();
        }
    }

    onImageError() {
        // Don't null previewUrl — the URL is still the intended image (may be a transient
        // S3 propagation lag). Track the error visually so the Re-crop button stays accessible.
        this.imageLoadError = true;
        this.cdr.markForCheck();
    }

    private updatePreview() {
        const imageId = this.parentFormGroup.get(this.field.name)?.value;
        this.revokeBlobUrl(this.previewUrl);
        this.previewUrl = this.imageService.url(imageId);
        this.imageLoadError = false; // reset error state when the intended image changes
    }

    private revokeBlobUrl(url: string | null) {
        if (url?.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    }

    private updateSourcePreview() {
        const sourceId = this.parentFormGroup.get(this.field.sourceField)?.value;
        this.sourcePreviewUrl = this.imageService.url(sourceId);
    }

    private async openCropper(input: { imageBase64?: string; imageURL?: string }): Promise<Blob | null> {
        const modal = await this.fileModalController.create({
            component: ImageCropperModal,
            componentProps: {
                imageBase64: input.imageBase64,
                imageURL: input.imageURL,
                cropWidth: this.field.crop?.width,
                cropHeight: this.field.crop?.height,
                minZoom: this.field.crop?.minZoom ?? 1
            },
            backdropDismiss: false
        });

        await modal.present();
        const { data } = await modal.onDidDismiss<Blob | null>();
        return data ?? null;
    }

    private getCroppedFilename(originalFilename: string): string {
        const dotIndex = originalFilename.lastIndexOf('.');
        const basename = dotIndex > 0 ? originalFilename.slice(0, dotIndex) : originalFilename;
        return `${basename}-cropped.png`;
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }
}
